// ⚠️ ARCHIVO GENERADO — NO LO EDITES AQUÍ.
// Fuente: /root/sites/_shared/lealify-campaigns/src/campaignRitmo.ts
// Para cambiarlo: edita la fuente y corre /root/sites/_shared/lealify-campaigns/sync.sh
// El deploy verifica esta copia con `sync.sh --check` y falla si difiere.
// lealify-campaigns/src/campaignRitmo.ts — FUENTE DE VERDAD
// ============================================================================
// RITMO Y CUPO: los textos que explican por qué el envío se frenó o cuánto queda.
//
// Este archivo se COPIA a los dos fronts con ./sync.sh — no edites las copias.
//
// Nace de un problema concreto: el 24-jul-2026 el cupo diario pasó a ser del NÚMERO
// (compartido entre campañas, cumpleaños y seguimientos) y hubo que explicarlo en las
// dos fachadas. Escribí las mismas frases dos veces el mismo día. Si mañana cambia el
// criterio y solo se corrige una, el mismo sistema le dice cosas distintas a dos
// clientes. Aquí vive el TEXTO; el JSX y los colores se quedan en cada front.
// ============================================================================
import type { AssistedQuota, NumberPace, FuenteEnvio } from './campaignMotorTypes';

/** Nombre legible de cada fuente proactiva, para decir quién se comió el cupo. */
const NOMBRE_FUENTE: Record<string, string> = {
  birthday: 'de cumpleaños',
  nurture: 'de seguimiento',
  campaign: 'de campañas',
};

/**
 * "3 de cumpleaños y 1 de seguimiento" — lo que consumió el cupo del número HOY
 * aparte de la campaña que se está mirando. Cadena vacía si no hay nada más.
 */
export function otrasFuentesDelDia(fuentes?: FuenteEnvio[] | null, excluir = 'campaign'): string {
  const otras = (fuentes || [])
    .filter((f) => f.clase === 'bulk' && f.fuente !== excluir && f.enviados > 0)
    .map((f) => `${f.enviados} ${NOMBRE_FUENTE[f.fuente] || f.fuente}`);
  if (!otras.length) return '';
  if (otras.length === 1) return otras[0];
  return `${otras.slice(0, -1).join(', ')} y ${otras[otras.length - 1]}`;
}

/**
 * Por qué no se puede enviar ahora, en una frase que el operador entienda.
 * `null` si sí hay cupo. El caso 'day' es el que más confunde: la campaña puede no
 * haber mandado nada y aun así estar frenada, porque el cupo es del WhatsApp.
 */
export function motivoDeFreno(q?: AssistedQuota | null): string | null {
  if (!q || q.remaining > 0) return null;
  switch (q.limited_by) {
    case 'hour':
      return `Límite por hora alcanzado (${q.hour_cap}). Espera un rato y sigue.`;
    case 'campaign':
      return `Esta campaña ya mandó sus ${q.day_cap} de hoy. Continúa mañana.`;
    case 'reserva':
      return 'El cupo que queda hoy está reservado para las felicitaciones de cumpleaños. Continúa mañana.';
    default:
      return `Este WhatsApp ya mandó sus ${q.day_cap} mensajes de hoy, contando campañas, cumpleaños y seguimientos. Continúa mañana.`;
  }
}

/**
 * POR QUÉ el lote de ahora es de N. Es la frase que faltaba: en un número nuevo
 * (10 al día · 3 por hora) el botón decía "Enviar a 3" justo debajo de un banner
 * que decía "lleva hoy 0 de 10", y los dos números parecían contradecirse. El 3
 * es el techo de la HORA. `null` cuando no hay cupo (ahí habla `motivoDeFreno`).
 */
export function textoLoteAhora(q?: AssistedQuota | null): string | null {
  if (!q || q.remaining <= 0) return null;
  const resto = typeof q.remaining_day === 'number' ? q.remaining_day : null;
  switch (q.binds_now) {
    case 'hour': {
      const luego = resto && resto > q.remaining
        ? ` Hoy quedan ${resto} en total: en un rato podrás seguir con el resto.`
        : '';
      return `Se mandan ${q.remaining} ahora porque el máximo por hora es ${q.hour_cap}.${luego}`;
    }
    case 'campaign':
      return `Se mandan ${q.remaining} ahora: es lo que le queda hoy a esta campaña.`;
    case 'day':
      return `Se mandan ${q.remaining} ahora: es lo que le queda hoy a este WhatsApp.`;
    default:
      return null;
  }
}

/** "10 al día · máximo 3 por hora" (o null mientras se calcula). */
export function textoRitmo(p?: NumberPace | null): string | null {
  return p ? `${p.day_cap} al día · máximo ${p.hour_cap} por hora` : null;
}

/** "Hoy este WhatsApp lleva 7 de 30 — incluye 4 de cumpleaños." */
export function textoConsumo(p?: NumberPace | null): string | null {
  if (!p || typeof p.usado_hoy !== 'number') return null;
  const otras = otrasFuentesDelDia(p.desglose_hoy?.por_fuente);
  return `Hoy este WhatsApp lleva ${p.usado_hoy} de ${p.day_cap}${otras ? ` — incluye ${otras}` : ''}.`;
}

/** Lo mismo pero desde el cupo de una campaña concreta (vista de audiencia). */
export function textoConsumoCupo(q?: AssistedQuota | null): string | null {
  if (!q) return null;
  const delNumero = q.used_today ?? q.sent_today;
  const aparte = q.used_today != null && q.used_today !== q.sent_today
    ? ` (${q.sent_today} de esta campaña)` : '';
  return `Hoy este WhatsApp lleva ${delNumero} de ${q.day_cap}${aparte}.`;
}
